import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, AIContentRequest, AIGeneratedContent } from '../lib/api'
import {
  Sparkles,
  Check,
  X,
  RefreshCw,
  Zap,
  Target,
  TrendingUp,
  BookOpen
} from 'lucide-react'
import { useForm } from 'react-hook-form'

export function AIContent() {
  const [showGenerator, setShowGenerator] = useState(false)
  const queryClient = useQueryClient()

  const { data: staging, isLoading } = useQuery({
    queryKey: ['ai-content', 'staging'],
    queryFn: () => api.getStagingQueue(),
  })

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.approveContent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-content', 'staging'] })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: (id: string) => api.rejectContent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-content', 'staging'] })
    },
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">AI Content Pipeline</h2>
          <p className="text-sm text-slate-500">Generate and moderate AI content</p>
        </div>
        <button
          onClick={() => setShowGenerator(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Sparkles className="w-4 h-4" />
          Generate Content
        </button>
      </div>

      {/* Moderation Queue */}
      <div className="card">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">
          Moderation Queue
          {staging && staging.length > 0 && (
            <span className="ml-2 badge-warning">{staging.length}</span>
          )}
        </h3>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2].map(i => (
              <div key={i} className="h-40 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : staging && staging.length > 0 ? (
          <div className="space-y-4">
            {staging.map((item) => (
              <ModerationCard
                key={item.id}
                item={item}
                onApprove={() => approveMutation.mutate(item.id)}
                onReject={() => rejectMutation.mutate(item.id)}
                isApproving={approveMutation.isPending}
                isRejecting={rejectMutation.isPending}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Queue is empty</h3>
            <p className="text-slate-500 mb-4">Generate some AI content to review.</p>
            <button
              onClick={() => setShowGenerator(true)}
              className="btn-primary"
            >
              Generate Content
            </button>
          </div>
        )}
      </div>

      {/* Generator Modal */}
      {showGenerator && (
        <GeneratorModal onClose={() => setShowGenerator(false)} />
      )}
    </div>
  )
}

function ModerationCard({
  item,
  onApprove,
  onReject,
  isApproving,
  isRejecting,
}: {
  item: AIGeneratedContent
  onApprove: () => void
  onReject: () => void
  isApproving: boolean
  isRejecting: boolean
}) {
  const [expanded, setExpanded] = useState(false)

  const platformIcons: Record<string, string> = {
    twitter: '𝕏',
    reddit: '🔴',
    linkedin: '💼',
    tiktok: '🎬',
    blog: '📝',
  }

  const aiProviderColors: Record<string, string> = {
    anthropic: 'bg-orange-100 text-orange-700',
    openai: 'bg-green-100 text-green-700',
    gemini: 'bg-blue-100 text-blue-700',
  }

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 bg-slate-50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xl">{platformIcons[item.originalRequest.platform]}</span>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-900">
                {item.generatedContent.title || item.originalRequest.contentType}
              </span>
              <span className={`badge ${aiProviderColors[item.aiProvider]}`}>
                {item.aiProvider}
              </span>
            </div>
            <p className="text-xs text-slate-500">
              {new Date(item.createdAt).toLocaleString()}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onReject}
            disabled={isRejecting}
            className="p-2 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
            title="Reject"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            onClick={onApprove}
            disabled={isApproving}
            className="p-2 hover:bg-green-100 text-green-600 rounded-lg transition-colors"
            title="Approve"
          >
            <Check className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Scores */}
      <div className="p-4 grid grid-cols-4 gap-4 border-b border-slate-200">
        <ScoreItem
          icon={Zap}
          label="Viral"
          value={item.metadata.estimatedViralScore}
          color="purple"
        />
        <ScoreItem
          icon={Target}
          label="SEO"
          value={item.metadata.seoScore}
          color="blue"
        />
        <ScoreItem
          icon={TrendingUp}
          label="Engagement"
          value={item.metadata.engagementPrediction}
          color="green"
        />
        <ScoreItem
          icon={BookOpen}
          label="Readability"
          value={item.metadata.readabilityScore}
          color="orange"
        />
      </div>

      {/* Content Preview */}
      <div className="p-4">
        <p className={`text-slate-700 whitespace-pre-wrap ${expanded ? '' : 'line-clamp-3'}`}>
          {item.generatedContent.body}
        </p>
        {item.generatedContent.body.length > 200 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-sm text-primary-600 hover:underline mt-2"
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}

        {item.generatedContent.hashtags && item.generatedContent.hashtags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {item.generatedContent.hashtags.map((tag, i) => (
              <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function ScoreItem({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: typeof Zap
  label: string
  value: number
  color: 'purple' | 'blue' | 'green' | 'orange'
}) {
  const colors = {
    purple: 'text-purple-600',
    blue: 'text-blue-600',
    green: 'text-green-600',
    orange: 'text-orange-600',
  }

  const bgColors = {
    purple: 'bg-purple-100',
    blue: 'bg-blue-100',
    green: 'bg-green-100',
    orange: 'bg-orange-100',
  }

  return (
    <div className="text-center">
      <div className={`w-8 h-8 ${bgColors[color]} rounded-full flex items-center justify-center mx-auto mb-1`}>
        <Icon className={`w-4 h-4 ${colors[color]}`} />
      </div>
      <p className="text-lg font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  )
}

function GeneratorModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const { register, handleSubmit } = useForm<AIContentRequest>({
    defaultValues: {
      platform: 'twitter',
      contentType: 'post',
      tone: 'engaging',
      length: 'medium',
      includeHashtags: true,
    },
  })

  const generateMutation = useMutation({
    mutationFn: (data: AIContentRequest) => api.generateAIContent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-content', 'staging'] })
      onClose()
    },
  })

  const onSubmit = (data: AIContentRequest) => {
    generateMutation.mutate(data)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900">Generate AI Content</h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Topic / Keywords
            </label>
            <input
              {...register('topic')}
              className="input"
              placeholder="What should the content be about?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Platform
              </label>
              <select {...register('platform')} className="input">
                <option value="twitter">Twitter/X</option>
                <option value="linkedin">LinkedIn</option>
                <option value="reddit">Reddit</option>
                <option value="tiktok">TikTok</option>
                <option value="blog">Blog</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Content Type
              </label>
              <select {...register('contentType')} className="input">
                <option value="post">Post</option>
                <option value="thread">Thread</option>
                <option value="article">Article</option>
                <option value="video_script">Video Script</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Tone
              </label>
              <select {...register('tone')} className="input">
                <option value="engaging">Engaging</option>
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="humorous">Humorous</option>
                <option value="educational">Educational</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Length
              </label>
              <select {...register('length')} className="input">
                <option value="short">Short</option>
                <option value="medium">Medium</option>
                <option value="long">Long</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Target Audience
            </label>
            <input
              {...register('targetAudience')}
              className="input"
              placeholder="Who is this content for?"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="hashtags"
              {...register('includeHashtags')}
              className="rounded border-slate-300"
            />
            <label htmlFor="hashtags" className="text-sm text-slate-700">
              Include hashtags
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={generateMutation.isPending}
              className="btn-primary flex items-center gap-2"
            >
              {generateMutation.isPending ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Generate
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
